import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useSelector } from "react-redux";

import QrCodePreview from "../Common/QrCodePreview";
import { const_var, params } from "../../redux/reducers/reducer";
import { makeUrlMobileReachable } from "../../services/arHosting.service";
import {
    canShareArModelFile,
    downloadArModel,
    exportBuildingSceneToGlb,
    exportBuildingSceneToUsdz,
    getARPlatform,
    getHostedARModelUrls,
    openAndroidSceneViewer,
    shareArModelFile,
} from "../../services/arViewer.service";

/* ─── styles ─────────────────────────────────────────────────────────── */

const cardStyle = {
    border: "1px solid #dfe5df",
    borderRadius: "12px",
    padding: "16px",
    marginBottom: "14px",
    background: "#f9fbf9",
};

const primaryBtn = {
    display: "block",
    width: "100%",
    borderRadius: "10px",
    border: "1px solid #1fac48",
    background: "#1fac48",
    color: "#ffffff",
    fontWeight: 700,
    fontSize: "16px",
    padding: "14px",
    cursor: "pointer",
    marginTop: "12px",
    textAlign: "center",
    textDecoration: "none",
};

const secondaryBtn = {
    ...primaryBtn,
    background: "#ffffff",
    color: "#1fac48",
};

const statusBox = (tone) => ({
    borderRadius: "10px",
    padding: "12px 14px",
    marginTop: "12px",
    fontSize: 14,
    ...(tone === "error"
        ? { background: "#fff7f7", border: "1px solid #efc7c7", color: "#9f1239" }
        : tone === "success"
        ? { background: "#f3fbf5", border: "1px solid #b9e4c4", color: "#166534" }
        : { background: "#f4f8ff", border: "1px solid #bfd4ff", color: "#1d4ed8" }),
});

/* ─── iOS view ───────────────────────────────────────────────────────── */
/**
 * Exports the live Three.js building scene to USDZ in the browser,
 * then renders a native <a rel="ar"> so the user can tap it directly.
 *
 * WHY: iOS Quick Look requires a direct native tap — any JS .click() or
 * async wrapper loses the user-gesture context and closes the camera
 * immediately. We pre-generate the USDZ blob URL up front so the anchor
 * is ready to tap without any async work on tap.
 */
const IosARView = () => {
    const [usdzBlobUrl, setUsdzBlobUrl] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const [status, setStatus] = useState(null);
    const blobUrlRef = useRef(null);

    // Clean up blob URL when component unmounts
    useEffect(() => {
        return () => {
            if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        };
    }, []);

    const generateUsdz = async () => {
        setIsExporting(true);
        setStatus({ tone: "info", text: "Exporting your building to USDZ — this takes a few seconds…" });
        try {
            const name = (params.building_type_name || "sensei-building")
                .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
            const fileName = `${name}-${params.p_w || 0}x${params.p_d || 0}x${params.p_h || 0}.usdz`;

            const { blobUrl } = await exportBuildingSceneToUsdz(const_var.scene, fileName);

            // Revoke previous blob if any
            if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = blobUrl;

            setUsdzBlobUrl(blobUrl);
            setStatus({ tone: "success", text: "Ready! Tap \"View in AR\" below to place the building in your space." });
        } catch (err) {
            setStatus({ tone: "error", text: err?.message || "Could not export the building. Please try again." });
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div style={{ padding: "4px 0 8px" }}>
            <div style={{ textAlign: "center", marginBottom: 16 }}>
                <div style={{ fontSize: 52 }}>🏗️</div>
                <h5 style={{ margin: "10px 0 4px", fontWeight: 700, color: "#111" }}>
                    View In Your Space
                </h5>
                <p style={{ color: "#555", fontSize: 14, margin: 0 }}>
                    Place your exact building in the real world using Apple AR.
                </p>
            </div>

            {/* Step 1: generate the USDZ */}
            {!usdzBlobUrl && (
                <button
                    type="button"
                    style={primaryBtn}
                    onClick={generateUsdz}
                    disabled={isExporting}
                >
                    {isExporting ? "Exporting building…" : "🏗️ Prepare for AR"}
                </button>
            )}

            {status && <div style={statusBox(status.tone)}>{status.text}</div>}

            {/* Step 2: USDZ ready — show instructions + native AR tap button */}
            {usdzBlobUrl && (
                <>
                    {/* How-to steps */}
                    <div style={{ background: "#f4f8ff", border: "1px solid #bfd4ff", borderRadius: 10, padding: "12px 14px", marginTop: 12, fontSize: 13, color: "#1e3a6e", lineHeight: 1.7 }}>
                        <div style={{ fontWeight: 700, marginBottom: 6 }}>How to use AR:</div>
                        <div>① Tap <strong>"View in AR"</strong> below</div>
                        <div>② Point camera at a <strong>flat floor</strong> — wait for a white dot</div>
                        <div>③ <strong>Tap the floor</strong> ~1 metre in front of you to place the model</div>
                        <div>④ <strong>Step back</strong> slightly to see the full building</div>
                        <div>⑤ Two fingers to <strong>move, rotate or resize</strong></div>
                    </div>

                    {/*
                     * CRITICAL: The user must tap THIS element directly.
                     * Do NOT trigger this via JS .click() — iOS will close
                     * Quick Look immediately if the gesture is not native.
                     */}
                    <a
                        rel="ar"
                        href={usdzBlobUrl}
                        style={{ ...primaryBtn, marginTop: 10 }}
                    >
                        <img
                            src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw=="
                            alt=""
                            style={{ display: "none" }}
                        />
                        📱 View in AR (Quick Look)
                    </a>

                    <button
                        type="button"
                        style={{ ...secondaryBtn, marginTop: 10 }}
                        onClick={generateUsdz}
                        disabled={isExporting}
                    >
                        ↺ Re-export
                    </button>
                </>
            )}
        </div>
    );
};

/* ─── Android view ───────────────────────────────────────────────────── */

const AndroidARView = ({ androidModelUrl }) => {
    const [status, setStatus] = useState(null);
    const [isPreparing, setIsPreparing] = useState(false);
    const [preparedFile, setPreparedFile] = useState(null);

    const launchSceneViewer = () => {
        if (!androidModelUrl) {
            setStatus({ tone: "error", text: "No Android GLB URL configured." });
            return;
        }
        const result = openAndroidSceneViewer(androidModelUrl, params.building_type_name || "Sensei Building");
        if (!result?.ok) setStatus({ tone: "error", text: result?.reason });
    };

    const prepareModel = async () => {
        setIsPreparing(true);
        setStatus(null);
        try {
            const name = (params.building_type_name || "sensei-building")
                .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
            const fileName = `${name}-${params.p_w || 0}x${params.p_d || 0}x${params.p_h || 0}.glb`;
            const file = await exportBuildingSceneToGlb(const_var.scene, fileName);
            setPreparedFile(file);
            setStatus({ tone: "success", text: "3D model ready." });
        } catch (err) {
            setStatus({ tone: "error", text: err?.message || "Could not export." });
        } finally {
            setIsPreparing(false);
        }
    };

    return (
        <div style={{ padding: "4px 0 8px" }}>
            <div style={{ textAlign: "center", marginBottom: 16 }}>
                <div style={{ fontSize: 52 }}>🏗️</div>
                <h5 style={{ margin: "10px 0 4px", fontWeight: 700, color: "#111" }}>
                    View In Your Space
                </h5>
                <p style={{ color: "#555", fontSize: 14, margin: 0 }}>
                    Opens Google Scene Viewer so you can place this building in your real environment.
                </p>
            </div>

            <button type="button" style={primaryBtn} onClick={launchSceneViewer}>
                📱 View in AR (Scene Viewer)
            </button>

            {status && <div style={statusBox(status.tone)}>{status.text}</div>}

            <div style={{ borderTop: "1px solid #e5e7eb", margin: "18px 0 12px" }} />

            <div style={{ color: "#666", fontSize: 13, marginBottom: 4 }}>
                Or export the current building as a 3D file:
            </div>
            <button
                type="button"
                style={secondaryBtn}
                onClick={prepareModel}
                disabled={isPreparing}
            >
                {isPreparing ? "Preparing…" : "Export 3D Model (GLB)"}
            </button>
            {preparedFile && (
                <button
                    type="button"
                    style={{ ...secondaryBtn, marginTop: 10 }}
                    onClick={() => downloadArModel(preparedFile)}
                >
                    Download GLB
                </button>
            )}
        </div>
    );
};

/* ─── Desktop view ───────────────────────────────────────────────────── */

const DesktopARView = ({ arConfig, qrTargetUrl }) => {
    const [isPreparing, setIsPreparing] = useState(false);
    const [preparedFile, setPreparedFile] = useState(null);
    const [status, setStatus] = useState(null);

    const prepareModel = async () => {
        setIsPreparing(true);
        setStatus(null);
        try {
            const name = (params.building_type_name || "sensei-building")
                .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
            const fileName = `${name}-${params.p_w || 0}x${params.p_d || 0}x${params.p_h || 0}.glb`;
            const file = await exportBuildingSceneToGlb(const_var.scene, fileName);
            setPreparedFile(file);
            setStatus({ tone: "success", text: "Building exported as GLB." });
        } catch (err) {
            setStatus({ tone: "error", text: err?.message || "Could not export the building model." });
        } finally {
            setIsPreparing(false);
        }
    };

    return (
        <div>
            <p style={{ color: "#666", marginTop: 4, marginBottom: 16, fontSize: 14 }}>
                Scan the QR code with your iPhone or Android to launch AR, or export the current building as a 3D file.
            </p>

            <QrCodePreview
                title="Scan on your phone to view in AR"
                targetUrl={qrTargetUrl}
                helperText="Opens the AR viewer on your iPhone (Quick Look) or Android (Scene Viewer)."
            />

            <div style={{ ...cardStyle, marginTop: 14 }}>
                <div style={{ fontWeight: 700, color: "#111", marginBottom: 6 }}>
                    Export 3D Model
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    <button
                        type="button"
                        style={{ ...primaryBtn, width: "auto", marginTop: 0 }}
                        onClick={prepareModel}
                        disabled={isPreparing}
                    >
                        {isPreparing ? "Preparing…" : "Prepare 3D Model"}
                    </button>
                    {preparedFile && (
                        <button
                            type="button"
                            style={{ ...secondaryBtn, width: "auto", marginTop: 0 }}
                            onClick={() => downloadArModel(preparedFile)}
                        >
                            Download GLB
                        </button>
                    )}
                </div>
                {status && <div style={statusBox(status.tone)}>{status.text}</div>}
            </div>

            <div style={{ ...cardStyle, fontSize: 13, color: "#555", marginTop: 0 }}>
                <span style={{ fontWeight: 700, color: "#111" }}>Hosted AR: </span>
                Android {arConfig.androidModelUrl ? "✅" : "❌"}&nbsp;|&nbsp;
                iPhone {arConfig.iosModelUrl ? "✅" : "❌"}
            </div>
        </div>
    );
};

/* ─── Main modal ─────────────────────────────────────────────────────── */

const ViewInYourSpaceModal = ({ show, onHide }) => {
    const leadcustomerState = useSelector((state) => state.leadcustomerReducer || {});
    const arPlatform = useMemo(() => getARPlatform(), []);
    const shareLink = leadcustomerState?.returnRespone?.share_link || "";

    const arConfig = useMemo(
        () =>
            getHostedARModelUrls({
                shareLink,
                buildingTypeName: params.building_type_name,
                width: params.p_w,
                length: params.p_d,
                height: params.p_h,
                manufacturerId: params.m_s_n,
            }),
        [shareLink]
    );

    const qrTargetUrl = useMemo(() => {
        if (typeof window === "undefined") return "";
        const baseUrl = `${window.location.origin}/#/ar-view`;
        const query = new URLSearchParams();
        if (shareLink) query.set("shareLink", shareLink);
        if (params.building_type_name) query.set("buildingTypeName", params.building_type_name);
        if (params.p_w != null) query.set("width", params.p_w);
        if (params.p_d != null) query.set("length", params.p_d);
        if (params.p_h != null) query.set("height", params.p_h);
        if (params.m_s_n != null) query.set("manufacturerId", params.m_s_n);
        return makeUrlMobileReachable(`${baseUrl}?${query.toString()}`);
    }, [shareLink]);

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            dialogClassName="react-modal"
            backdropClassName="ract-modal-pre-parent"
        >
            <Modal.Body style={{ paddingTop: 28 }}>
                <div className="close" onClick={onHide}>
                    <i className="icon-cross-ico"></i>
                </div>
                <div style={{ padding: "4px 8px 8px" }}>
                    {arPlatform === "ios" && (
                        <IosARView />
                    )}
                    {arPlatform === "android" && (
                        <AndroidARView androidModelUrl={arConfig.androidModelUrl} />
                    )}
                    {arPlatform === "desktop" && (
                        <DesktopARView arConfig={arConfig} qrTargetUrl={qrTargetUrl} />
                    )}
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default ViewInYourSpaceModal;
